import { HttpClient } from '@angular/common/http';
import { ModalService } from 'src/app/services/modal.service';
import { StorageService } from 'src/app/services/storage.service';
import { environment } from 'src/environments/environment';
import { api } from 'src/whiteLabel/mediaCorps/services/misc/misc-api.service';

const CUSTOMER_ID = 'CUSTOMER_ID';
const BRAND_ID = 'BRAND_ID';
const ADVERTISER_ID = 'ADVERTISER_ID';
const IS_MULTI_ADVERTISER = 'IS_MULTI_ADVERTISER';

export class BaseAdapter {
    private customerId: number;
    private brandId: number;
    private advertiserId: number;

    constructor(
        protected httpClient: HttpClient,
        protected localStore: StorageService,
        protected modal: ModalService,
    ) {
    }

    getCustomerId(): Promise<number | null> {
        if (!environment.loadCBA) {
            return Promise.resolve(environment.customerId);
        }

        return Promise.resolve(this.localStore.getItem(CUSTOMER_ID, 'session'));

    }

    getBrandId(): Promise<number | null> {
        if (!environment.loadCBA) {
            return Promise.resolve(environment.brandId);
        }

        return Promise.resolve(this.localStore.getItem(BRAND_ID, 'session'));
    }

    getAdvertiserId(): Promise<number | null> {
        if (!environment.loadCBA) {
            return Promise.resolve(environment.advertiserId);
        }

        return Promise.resolve(this.localStore.getItem(ADVERTISER_ID, 'session'));

    }

    getCBAId(): Promise<{ customerId: number, brandId: number, advertiserId: number }> {
        return this.getCustomerId()
            .then(customerId => {
                return this.getAdvertiserId()
                    .then(advertiserId => {
                        return this.getBrandId()
                            .then(brandId => {
                                return {
                                    customerId, advertiserId, brandId
                                }
                            })
                    })
            });
    }

    getCustomers(): Promise<any> {
        // check local storage first
        let customers = this.localStore.getItem('getCustomers()', 'session');

        if (customers && customers.length > 0) {
            // already cached
            return Promise.resolve(customers);
        } else {
            // fetching id from api
            return api.getCustomers(this.httpClient, environment.platformClientId).then(customers => {
                this.localStore.setItem('getCustomers()', customers, 'session');
                return customers;
            }).catch((e) => {
                if (e && e.error && e.error.Errors && e.error.Errors[0] &&
                    e.error.Errors[0].Code === 'UserIsNotAllowedToAccessTheSpecifiedPlatform') {
                    this.modal.info({
                        message: 'Welcome to AdDirect! It seems like your account is not active yet. Please drop us an email at addirect@mediacorp.com.sg and we will get back to you as soon as possible.'
                    });
                }
                throw e;
            });
        }
    }

    getAdvertiserIds(customerId: number): Promise<any> {
        // check local storage first
        let advertisers = this.localStore.getItem('getAdvertisers()ByCustomerId-' + customerId, 'session');

        if (advertisers && advertisers.length > 0) {
            // already cached
            return Promise.resolve(advertisers);
        } else {
            // fetching id from api
            return api.getAdvertisers(this.httpClient, customerId).then(advertisers => {
                this.localStore.setItem('getAdvertisers()ByCustomerId-' + customerId, advertisers, 'session');
                return advertisers;
            });
        }
    }

    getBrandIds(customerId: number, advertiserId: number): Promise<any> {
        // check local storage first
        let brands = this.localStore.getItem('getBrands()ByCustomerId-' + customerId + 'ByAdvertiserId-' + advertiserId, 'session');

        if (brands && brands.length > 0) {
            // already cached
            return Promise.resolve(brands);
        } else {
            // fetching id from api
            return api.getBrands(this.httpClient, environment.platformClientId, customerId, advertiserId).then(brands => {
                this.localStore.setItem('getBrands()ByCustomerId-' + customerId + 'ByAdvertiserId-' + advertiserId, brands, 'session');
                return brands;
            });

        }
    }

    updateCBAId(customerId: number, advertiserId: number, brandId: number) {
        if (customerId && customerId > 0) {
            let prevCustomerId = Number(this.localStore.getItem(CUSTOMER_ID, 'session'));
            if (prevCustomerId && prevCustomerId !== customerId) {
                // clear child id
                this.advertiserId = undefined;
                this.brandId = undefined;
                this.localStore.removeItem(ADVERTISER_ID, 'session');
                this.localStore.removeItem(BRAND_ID, 'session');
            }
            this.customerId = customerId;
            this.localStore.setItem(CUSTOMER_ID, customerId, 'session');
        }
        if (advertiserId && advertiserId > 0) {
            let prevAdvertiserId = Number(this.localStore.getItem(ADVERTISER_ID, 'session'));
            if (prevAdvertiserId && prevAdvertiserId !== advertiserId) {
                // clear child id
                this.brandId = undefined;
                this.localStore.removeItem(BRAND_ID, 'session');
            }
            this.advertiserId = advertiserId;
            this.localStore.setItem(ADVERTISER_ID, advertiserId, 'session');
        }
        if (brandId && brandId > 0) {
            this.brandId = brandId;
            this.localStore.setItem(BRAND_ID, brandId, 'session');
        }
    }

    //use this to determine if we need to render multi-advertiser UI
    isMultiAdvertiser(): Promise<boolean> {
        let isMultiAdvertiser = this.localStore.getItem(IS_MULTI_ADVERTISER, 'session');
        if (isMultiAdvertiser != undefined) {
            // already cached
            return Promise.resolve(isMultiAdvertiser);
        } else {
            return this.getCustomers().then(customers => {
                let isMultiAdvertiser: boolean = false;
                if (customers.length > 1) {
                    isMultiAdvertiser = true;
                    this.localStore.setItem(IS_MULTI_ADVERTISER, isMultiAdvertiser, 'session')
                    return isMultiAdvertiser;
                } else {
                    this.updateCBAId(customers[0].Id, null, null);
                    return this.getAdvertiserIds(customers[0].Id).then(advertisers => {
                        if (advertisers.length > 1) {
                            isMultiAdvertiser = true;
                            this.localStore.setItem(IS_MULTI_ADVERTISER, isMultiAdvertiser, 'session');
                            return isMultiAdvertiser;
                        } else {
                            this.updateCBAId(null, advertisers[0].Id, null);
                            return this.getBrandIds(customers[0].Id, advertisers[0].Id).then(brands => {
                                if (brands.length > 1) {
                                    isMultiAdvertiser = true;
                                    this.localStore.setItem(IS_MULTI_ADVERTISER, isMultiAdvertiser, 'session');
                                    return isMultiAdvertiser;
                                } else {
                                    this.updateCBAId(null, null, brands[0].Id);
                                    this.localStore.setItem(IS_MULTI_ADVERTISER, isMultiAdvertiser, 'session');
                                    return isMultiAdvertiser;
                                }
                            });
                        }
                    });
                }
            });
        }
    }

    public getConnections(): Promise<any> {
        return api.getConnections(this.httpClient, environment.platformClientId);
    }

}
